/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { StatusBar, SubmitButton } from '../PersonalBusinessReviewView.styles';

const SelectButton = styled.button`
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  color: #6b7280;
  cursor: pointer;
  font-size: 12px;
  margin-left: 4px;
  padding: 2px 6px;

  &:hover {
    background-color: #f3f4f6;
    border-color: #9ca3af;
    color: #374151;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

interface PersonalBusinessReviewStatusBarProps {
  filteredTotal: number;
  totalEntries: number;
  personalCount: number;
  businessCount: number;
  selectedCount: number;
  selectedBusinessCount: number;
  selectedPersonalCount: number;
  isLoading: boolean;
  isClassifyLoading: boolean;
  isConfirmLoading: boolean;
  hasPendingClassifications: boolean;
  onMarkAsBusiness: () => void;
  onMarkAsPersonal: () => void;
  onSubmitToBooks: () => void;
  onSelectAllBusiness: () => void;
  onSelectAllPersonal: () => void;
  onClearSelection: () => void;
}

export const PersonalBusinessReviewStatusBar = React.memo<PersonalBusinessReviewStatusBarProps>(
  ({
    filteredTotal,
    totalEntries,
    personalCount,
    businessCount,
    selectedCount,
    selectedBusinessCount,
    selectedPersonalCount,
    isLoading,
    isClassifyLoading,
    isConfirmLoading,
    hasPendingClassifications,
    onMarkAsBusiness,
    onMarkAsPersonal,
    onSubmitToBooks,
    onSelectAllBusiness,
    onSelectAllPersonal,
    onClearSelection,
  }) => {
    const isSelectDisabled = isLoading || isClassifyLoading || hasPendingClassifications;

    const statusElements = useMemo(() => {
      const elements: React.ReactNode[] = [];

      elements.push(`${filteredTotal} of ${totalEntries} transactions`);

      if (personalCount > 0) {
        elements.push(
          <span key="personal">
            {personalCount} Personal
            <SelectButton
              onClick={onSelectAllPersonal}
              disabled={isSelectDisabled}
              title="Select all personal transactions"
            >
              Select
            </SelectButton>
          </span>,
        );
      }

      if (businessCount > 0) {
        elements.push(
          <span key="business">
            {businessCount} Business
            <SelectButton
              onClick={onSelectAllBusiness}
              disabled={isSelectDisabled}
              title="Select all business transactions"
            >
              Select
            </SelectButton>
          </span>,
        );
      }

      if (selectedCount > 0) {
        elements.push(
          <span key="selected">
            {selectedCount} Selected
            <SelectButton
              onClick={onClearSelection}
              disabled={isSelectDisabled}
              title="Clear selection"
            >
              Clear
            </SelectButton>
          </span>,
        );
      }

      if (isClassifyLoading || hasPendingClassifications) {
        elements.push('Saving changes...');
      }

      return elements;
    }, [
      filteredTotal,
      totalEntries,
      personalCount,
      businessCount,
      selectedCount,
      isClassifyLoading,
      hasPendingClassifications,
      isSelectDisabled,
      onSelectAllBusiness,
      onSelectAllPersonal,
      onClearSelection,
    ]);

    const isMarkAsBusinessDisabled = useMemo(
      () =>
        isLoading || isClassifyLoading || hasPendingClassifications || selectedPersonalCount === 0,
      [isLoading, isClassifyLoading, hasPendingClassifications, selectedPersonalCount],
    );

    const isMarkAsPersonalDisabled = useMemo(
      () =>
        isLoading || isClassifyLoading || hasPendingClassifications || selectedBusinessCount === 0,
      [isLoading, isClassifyLoading, hasPendingClassifications, selectedBusinessCount],
    );

    const isSubmitDisabled = useMemo(
      () =>
        isLoading ||
        isClassifyLoading ||
        isConfirmLoading ||
        hasPendingClassifications ||
        selectedCount === 0,
      [isLoading, isClassifyLoading, isConfirmLoading, hasPendingClassifications, selectedCount],
    );

    return (
      <StatusBar>
        <div>
          {statusElements.map((element, index) => (
            <React.Fragment key={index}>
              {index > 0 && ' • '}
              {element}
            </React.Fragment>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <SubmitButton
            onClick={onMarkAsBusiness}
            disabled={isMarkAsBusinessDisabled}
            $bgColor="#059669"
            $borderColor="#059669"
          >
            Mark as Business ({selectedPersonalCount})
          </SubmitButton>
          <SubmitButton
            onClick={onMarkAsPersonal}
            disabled={isMarkAsPersonalDisabled}
            $bgColor="#7c3aed"
            $borderColor="#7c3aed"
          >
            Mark as Personal ({selectedBusinessCount})
          </SubmitButton>
          <SubmitButton
            onClick={onSubmitToBooks}
            disabled={isSubmitDisabled}
            $bgColor="#2563eb"
            $borderColor="#2563eb"
            $isLoading={isConfirmLoading}
          >
            {isConfirmLoading ? 'Submitting...' : `Submit Selected (${selectedCount})`}
          </SubmitButton>
        </div>
      </StatusBar>
    );
  },
);

PersonalBusinessReviewStatusBar.displayName = 'PersonalBusinessReviewStatusBar';
